import { apiClient } from '@/lib/api/client'

export type CaseParty = {
  id: string
  label: string
  code?: string | null
  email?: string | null
  request_count: number
  unread_count?: number
  latest_at?: string | null
}

export type CaseRequest = {
  id: number
  request_number: string
  subject: string | null
  original_subject?: string | null
  status: string
  status_code: number
  parent_status?: string | null
  parent_status_label?: string | null
  display_status?: string | null
  contact_name: string | null
  contact_email: string | null
  client_id: number | null
  parent_ticket_id: number | null
  needs_human_review: boolean
  preview: string | null
  unread_count: number
  updated_at: string | null
}

export type CaseMessage = {
  id: number
  direction: 'inbound' | 'outbound' | 'system' | 'internal_note'
  from_name?: string | null
  from_email: string | null
  to: string[]
  cc: string[]
  subject: string | null
  body_preview: string | null
  received_at: string | null
  sent_at: string | null
  created_at: string | null
  attachments: { id: number; filename: string; mime: string | null; size: number | null }[]
}

export type QueueEmail = {
  id: number
  subject: string | null
  from_name: string | null
  from_email: string | null
  body_preview: string | null
  received_at: string | null
  triage_status: string | null
  match_confidence: number | null
  suggested_ticket_request_id: number | null
  suggested_request_number: string | null
  reason: string | null
}

export type AttentionSummary = {
  unread_count: number
  unlinked_count: number
  needs_review_count: number
  total: number
  latest_id: number | null
  latest_subject: string | null
  latest_from: string | null
}

export async function getCaseThreadAttention(): Promise<AttentionSummary> {
  const { data } = await apiClient.get<{ data: AttentionSummary }>('/support/case-threads/attention')
  return data.data
}

export async function getCaseParties(groupBy: 'client' | 'requestor') {
  const { data } = await apiClient.get<{ data: CaseParty[]; meta: { unlinked_count: number; needs_review_count: number } }>(
    '/support/case-threads/parties',
    { params: { group_by: groupBy } },
  )
  return data
}

export async function getPartyRequests(groupBy: 'client' | 'requestor', partyId: string) {
  const { data } = await apiClient.get<{ data: CaseRequest[] }>(
    `/support/case-threads/parties/${encodeURIComponent(partyId)}/requests`,
    { params: { group_by: groupBy } },
  )
  return data.data
}

export async function getParentStatusLabel(parentTicketId: number): Promise<string | null> {
  const { data } = await apiClient.get<{ data?: { status_label?: string | null } }>(`/tickets/${parentTicketId}`)
  const label = data?.data?.status_label
  return typeof label === 'string' && label.trim() !== '' ? label.trim() : null
}

export async function getRequestMessages(requestId: number) {
  const { data } = await apiClient.get<{
    data: { request: CaseRequest; locked_subject: string; participants: string[]; messages: CaseMessage[]; parent_ticket_id: number | null }
  }>(`/support/case-threads/requests/${requestId}/messages`)
  return data.data
}

export async function getThreadMessageBody(messageId: number): Promise<string> {
  const { data } = await apiClient.get<{ data: { body: string } }>(`/support/case-threads/messages/${messageId}/body`)
  return data.data.body || ''
}

export async function replyInThread(
  requestId: number,
  body: string,
  cc?: string,
  replyAll?: boolean,
  attachments: File[] = []
) {
  const form = new FormData()
  form.append('body', body)
  if (cc?.trim()) form.append('cc', cc.trim())
  form.append('reply_all', replyAll ? '1' : '0')
  for (const file of attachments) {
    form.append('attachments[]', file)
  }
  const { data } = await apiClient.post(`/support/case-threads/requests/${requestId}/reply`, form)
  return data
}

export async function addInternalNote(requestId: number, body: string) {
  await apiClient.post(`/support/case-threads/requests/${requestId}/notes`, { body })
}

export async function markRequestWaiting(requestId: number) {
  await apiClient.post(`/support/case-threads/requests/${requestId}/mark-waiting`)
}

export type RequestOption = {
  id: number
  request_number: string
  subject: string | null
  contact_name: string | null
  contact_email: string | null
}

export async function searchRequestOptions(search: string, includeId?: number | null): Promise<RequestOption[]> {
  const { data } = await apiClient.get<{ data: RequestOption[] }>('/support/case-threads/request-options', {
    params: {
      ...(search.trim() ? { search: search.trim() } : {}),
      ...(includeId ? { include_id: includeId } : {}),
    },
  })
  return data.data
}

export async function getQueue(kind: 'unlinked' | 'needs-review') {
  const { data } = await apiClient.get<{ data: QueueEmail[] }>(`/support/case-threads/${kind}`)
  return data.data
}

export async function attachEmail(emailId: number, ticketRequestId: number) {
  const { data } = await apiClient.post(`/support/inbox/${emailId}/attach`, { ticket_request_id: ticketRequestId })
  return data
}

export async function createRequestFromEmail(emailId: number, clientId: number) {
  const { data } = await apiClient.post(`/support/inbox/${emailId}/create-request`, { client_id: clientId })
  return data
}

export async function ignoreEmail(emailId: number) {
  await apiClient.post(`/support/inbox/${emailId}/ignore`)
}
